import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';

// TES Dashboard URL - the deployed takeback analytics dashboard
const TES_DASHBOARD_URL = 'https://takeback-dashboard.philip-134.workers.dev';

export const Route = createFileRoute('/takeback-dashboard')({
  component: TakebackAnalytics,
});

function TakebackAnalytics() {
  const [isLoading, setIsLoading] = useState(true);
  const [programs, setPrograms] = useState<Array<{ id: string; name: string; external_id: string | null }>>([]);
  const [selectedProgram, setSelectedProgram] = useState<string>('');

  useEffect(() => {
    // Fetch takeback programs to show program selector
    fetch('/api/programs')
      .then(res => res.json())
      .then(data => {
        const takebackPrograms = (data.programs || []).filter(
          (p: { type: string; status: string }) => 
            (p.type === 'takeback' || p.type === 'trade_in') && p.status !== 'archived'
        );
        setPrograms(takebackPrograms);
        if (takebackPrograms.length > 0) {
          setSelectedProgram(takebackPrograms[0].id);
        }
      })
      .catch(console.error);
  }, []);

  const selectedProgramData = programs.find(p => p.id === selectedProgram);
  const tesClientId = selectedProgramData?.external_id;

  // Build TES dashboard URL with metrics route
  const dashboardUrl = tesClientId 
    ? `${TES_DASHBOARD_URL}/metrics?dateRange=all_time`
    : TES_DASHBOARD_URL;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-light" style={{ color: '#171717' }}>Takeback Analytics</h1>
          <p className="text-sm mt-1" style={{ color: '#A9A9A9' }}>
            Full analytics dashboard for takeback and trade-in programs
          </p>
        </div>

        {/* Program Selector */}
        {programs.length > 1 && (
          <select
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            className="px-4 py-2 border  text-sm"
            style={{ borderColor: 'rgba(169,169,169,0.3)' }}
          >
            {programs.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Connection Notice */}
      {programs.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center" style={{ color: '#A9A9A9' }}>
            <TakebackIcon size={64} />
            <h2 className="text-lg font-medium mt-4" style={{ color: '#171717' }}>
              No Takeback Programs
            </h2>
            <p className="mt-2">Create a takeback program and connect it to TES to view analytics</p>
            <a 
              href="/programs"
              className="inline-block mt-4 px-4 py-2  text-sm font-medium"
              style={{ backgroundColor: '#00FBA9', color: '#171717' }}
            >
              Create Program
            </a>
          </div>
        </div>
      ) : !tesClientId ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center" style={{ color: '#A9A9A9' }}>
            <ConnectionIcon size={64} />
            <h2 className="text-lg font-medium mt-4" style={{ color: '#171717' }}>
              Program Not Connected
            </h2>
            <p className="mt-2">Connect "{selectedProgramData?.name}" to TES to view analytics</p>
            <a 
              href={`/programs/${selectedProgram}`}
              className="inline-block mt-4 px-4 py-2  text-sm font-medium"
              style={{ backgroundColor: '#00FBA9', color: '#171717' }}
            >
              Connect to TES
            </a>
          </div>
        </div>
      ) : (
        <>
          {/* TES Dashboard Embed */}
          <div className="flex-1  overflow-hidden border" style={{ borderColor: 'rgba(169,169,169,0.2)', minHeight: 'calc(100vh - 200px)' }}>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white">
                <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" 
                  style={{ borderColor: '#00FBA9', borderTopColor: 'transparent' }} 
                />
              </div>
            )}
            <iframe
              src={dashboardUrl}
              title="Takeback Analytics Dashboard"
              className="w-full h-full border-0"
              style={{ minHeight: 'calc(100vh - 200px)' }}
              onLoad={() => setIsLoading(false)}
              allow="clipboard-write"
            />
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <p className="text-xs" style={{ color: '#A9A9A9' }}>
              Connected to TES client: {tesClientId}
            </p>
            <a
              href={dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm flex items-center gap-1"
              style={{ color: '#00FBA9' }}
            >
              Open in new tab
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </>
      )}
    </div>
  );
}

// SVG Icons
function TakebackIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#A9A9A9" strokeWidth="1.5" className="mx-auto">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2" strokeLinecap="round"/>
      <path d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22" strokeLinecap="round" strokeDasharray="3 3"/>
      <path d="M8 12L12 8L16 12" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 8V16" strokeLinecap="round"/>
    </svg>
  );
}

function ConnectionIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#A9A9A9" strokeWidth="1.5" className="mx-auto">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

